import { Chart as ChartJS } from "chart.js";
import React, { useEffect, useState } from "react";
import { Line } from "react-chartjs-2";

import PyTooltip from "../../../../../../../../../components/Tooltip";
import { labelsPlugin } from "./LineChartPlugins";

import { getColor } from "../../../../../../../../../utils/getColor";
import { useTruncatedText } from "../../../../../../../../../utils/textMeasurement/useTruncatedText";

import css from "../../Layout/Layout.module.scss";
import { drawTopBorderPlugin } from "../BarChart/BarChartPlugins";
import { Label } from "../UseChartOptions/useChartOptions";

ChartJS.defaults.font.family = "Source Sans Pro, sans-serif";
export interface LineChartProps {
	labels?: Label[];
	// Trendline (v1) feeds undefined for unfilled time buckets; the chat (v2) feeds null for
	// empty-group aggregates. Both are genuine runtime values, so both are typed honestly here.
	datasets?: (number | null | undefined)[];
	xLabelText?: string;
	yLabelText?: string;
	isCountAggregation?: boolean;
	// Chart.js option: whether the line bridges over null/undefined points. Defaults to true to
	// preserve the v1 Visualize Panel trendline's continuous-line behavior; the chat surface
	// opts out (spanGaps={false}) so a missing group renders as a real gap, not an implied trend.
	spanGaps?: boolean;
	// Chart.js option: floor for the y-axis. Defaults to 1 to preserve the v1 Visualize Panel
	// trendline's current axis (its data is count-based and never sub-1); the chat opts out with
	// `null` — no floor at all, so beginAtZero still includes zero but the axis is free to extend
	// below it, since a numeric custom field's average/sum/minimum can legitimately be 0 or negative
	// and a hard floor would clip those the same way the old unconditional min:1 clipped zeros.
	yAxisMin?: number | null;
}

// A bar chart that handles avatar images, horizontal, vertical bar and tooltips
const LineChart = React.forwardRef<any, LineChartProps>((props, ref: any) => {
	const { labels, datasets = [], xLabelText, yLabelText, isCountAggregation, spanGaps, yAxisMin = 1 } = props;
	const [canvasWidth, setCanvasWidth] = useState(0);
	const [canvasHeight, setCanvasHeight] = useState(0);

	const { didTruncateText: didTruncateXLabel } = useTruncatedText(
		xLabelText || "",
		canvasWidth * 0.7 || 500,
		"paragraph-2",
		"regular"
	);
	const { didTruncateText: didTruncateYLabel } = useTruncatedText(
		yLabelText || "",
		canvasHeight * 0.7 || 500,
		"paragraph-2",
		"regular"
	);

	useEffect(() => {
		// Observe size changes
		const resizeObserver = new ResizeObserver((entries) => {
			const { width, height } = entries[0].contentRect;

			// Delay the update to fix the issue where the canvas width are not updated
			requestAnimationFrame(() => {
				setCanvasWidth(width);
				setCanvasHeight(height);
				ref?.current?.update();
			});
		});

		resizeObserver.observe(ref?.current.canvas);

		return () => {
			resizeObserver.disconnect();
		};
	}, [xLabelText, yLabelText, ref?.current]);

	const chartLabels = labels?.slice() || [];

	const tooltip = {
		displayColors: false,
		enabled: true,
		backgroundColor: getColor("neutral", 7),
		callbacks: {
			title: function (context) {
				const labelData = chartLabels[context[0].dataIndex];
				return labelData?.name || "-";
			},
			label: function (context) {
				const labelData = context.raw as number;

				if (isCountAggregation) {
					const formCount = labelData ? labelData.toString() : "0";
					const formLabel = labelData === 1 ? "Form" : "Forms";
					return formCount + " " + formLabel;
				}
				return labelData ? labelData.toFixed(2) : "0";
			},
		},
	};

	const options = {
		responsive: true,
		maintainAspectRatio: false,
		devicePixelRatio: 2,
		spanGaps: spanGaps ?? true,
		plugins: {
			legend: {
				display: true,
			},
			tooltip,
		},
		scales: {
			y: {
				display: true,
				beginAtZero: true,
				ticks: {
					stepSize: 1,
					padding: 8,
					font: {
						size: 12,
						family: "Source Sans Pro, sans-serif",
					},
				},
				// `null` must reach Chart.js as `undefined`, never as the literal null: this repo's
				// chart.js (LinearScaleBase.parse → getUserBounds) resolves either the same way here,
				// but making the conversion explicit keeps the "no floor" intent from depending on
				// that continuing to hold across a chart.js upgrade.
				min: yAxisMin ?? undefined,
				grid: {
					drawTicks: false,
					color: getColor("neutral", 2),
					lineWidth: 1,
				},
				border: {
					color: getColor("neutral", 2),
				},
			},
			x: {
				beginAtZero: true,
				ticks: {
					padding: 8,
					font: {
						size: 12,
						family: "Source Sans Pro, sans-serif",
					},
					maxTicksLimit: 35,
				},
				grid: {
					drawTicks: false,
					color: getColor("neutral", 2),
					lineWidth: 1,
				},
				border: {
					color: getColor("neutral", 2),
				},
			},
		},
		layout: {
			padding: {
				top: 16,
				left: 50,
				right: 16,
				bottom: 120,
			},
		},
	};

	return (
		<div className={css.relativeContainer}>
			<Line
				data-testid="lineChart"
				ref={ref}
				options={options}
				data={{
					labels: chartLabels.map((label) => label.name),
					datasets: [
						{
							data: datasets,
							backgroundColor: getColor("primary", 5),
							borderColor: getColor("primary", 5),
							pointBackgroundColor: getColor("primary", 5),
							borderWidth: 2,
							clip: 8,
						},
					],
				}}
				plugins={[labelsPlugin(xLabelText, yLabelText), drawTopBorderPlugin]}
			/>

			{didTruncateXLabel && (
				<div className={css.horizontalBarTooltip}>
					<PyTooltip message={xLabelText || ""}>
						<div className={css.tooltipTextPlaceholder} style={{ width: canvasWidth * 0.7 }} />
					</PyTooltip>
				</div>
			)}

			{didTruncateYLabel && (
				<div className={css.barTooltip}>
					<PyTooltip message={yLabelText || ""}>
						<div className={css.tooltipVerticalTextPlacholder} style={{ height: canvasHeight * 0.7 }} />
					</PyTooltip>
				</div>
			)}
		</div>
	);
});

LineChart.displayName = "LineChart";

export default React.memo(LineChart);
